import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AlertCircle, Send, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Submit = () => {
  const { user, token, setUser } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [promote, setPromote] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      toast.error('Please enter post content');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/posts/create`,
        { content, promote },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const userRes = await axios.get(`${API}/profile`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setUser(userRes.data);
      
      toast.success('Post created successfully!');
      setContent('');
      setPromote(false);
      navigate('/home');
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('No credits available. Redirecting to payment...');
        setTimeout(() => navigate('/payment'), 2000);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to create post');
      }
    } finally {
      setLoading(false);
    }
  };

  const isPremium = user?.role === 'premium';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="submit-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 page-header-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Submit a Post</h1>
            <p className="text-gray-600 dark:text-slate-400">Share your thoughts anonymously</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Submit Form */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-black/20 dark:border-white/20 p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="content" className="text-gray-900 dark:text-slate-100">Your Post</Label>
                  <Textarea
                    id="content"
                    data-testid="post-content"
                    placeholder="Share your thoughts... (Remember: Don't share personal information)"
                    value={content}
                    rows={8}
                    maxLength={250}
                    onChange={(e) => setContent(e.target.value)}
                    className="mt-2 resize-none bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-cyan-500"
                  />
                  <div className="text-right text-sm mt-1">
                    <span
                      className={content.length >= 240 ? 'text-red-600' : 'text-gray-500 dark:text-slate-400'}
                      >
                        {content.length}/250
                      </span>
                  </div>
                  <div className="flex items-start gap-2 mt-2 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      Do not share personal information like your name, email, phone number, or address.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="promote"
                    data-testid="promote-checkbox"
                    checked={promote}
                    onCheckedChange={setPromote}
                    disabled={isPremium}
                    className="h-5 w-5 rounded border-2 border-gray-400 dark:border-slate-400 bg-white dark:bg-slate-700 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500 dark:data-[state=checked]:bg-cyan-500 dark:data-[state=checked]:border-cyan-500 focus-visible:ring-2 focus-visible:ring-cyan-500 transition-colors"/>
                  <Label htmlFor="promote" className="cursor-pointer text-gray-900 dark:text-slate-100">
                    Promote this post {!isPremium && `(${user?.credits || 0} credits available)`}
                  </Label>
                </div>

                {isPremium && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg">
                    <p className="text-sm text-purple-800 dark:text-purple-200">
                      ⭐ As a premium user, your posts are already promoted!
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="submit-post-btn"
                  className="w-full border border-black/30 dark:border-white/30 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700"
                  disabled={
                    loading ||
                    content.trim().length === 0 ||
                    content.length > 250
                  }
                >
                  {loading ? 'Posting...' : (
                    <>
                      <Send className="w-4 h-4 mr-2 text-black dark:text-white" />
                      <span className="text-black dark:text-white">Post</span>
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Preview */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-black/20 dark:border-white/20 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-gray-600 dark:text-slate-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Preview</h3>
              </div>
              
              <div
                className={`relative p-6 rounded-xl border-2 transition-colors
                  ${
                    isPremium
                      ? `
                        border-black/20 dark:border-white/20
                        bg-gradient-to-br
                        from-purple-50 via-pink-50 to-blue-50
                        dark:from-purple-900/30 dark:via-pink-900/30 dark:to-blue-900/30
                      `
                      : promote
                      ? `
                        border-yellow-200 bg-yellow-50/30
                        dark:border-yellow-600 dark:bg-yellow-900/20
                      `
                      : `
                        border-gray-200 bg-gray-50
                        dark:border-slate-700 dark:bg-slate-900
                      `
                  }
                `}
              >
                {isPremium && (
                  <div className="absolute top-2 right-2 dark:opacity-90">
                    <span className="text-yellow-500 text-xl">⭐</span>
                  </div>
                )}
                {promote && !isPremium && (
                  <div className="mb-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1  bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 text-xs font-medium rounded-full">
                      ⚡ Promoted
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={user?.avatar_url}
                    alt={user?.username}
                    className={`w-12 h-12 rounded-full ${isPremium ? 'ring-4 ring-purple-400' : ''}`}
                  />
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">{user?.username}</div>
                    <div className="text-sm text-gray-500 dark:text-slate-400">Just now</div>
                  </div>
                </div>
                <p className="text-gray-800 dark:text-slate-100 whitespace-pre-wrap"
                  style={{
                    wordBreak: 'normal',
                    overflowWrap: 'anywhere',
                  }}
                  >
                  {content || 'Your post content will appear here...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Submit;